import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import CoinForm from '../components/CoinForm';
import BulkImportForm from '../components/BulkImportForm';
import BatchCoinForm from '../components/BatchCoinForm';
import { coins } from '../utils/api';

const AddCoin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('manual'); // 'manual', 'bulk', or 'batch'
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Current user:', user); // Debug log
      console.log('Submitting data:', data); // Debug log

      // Format data before sending
      const formattedData = {
        ...data,
        certification_service_id: data.certification_service_id ? Number(data.certification_service_id) : null,
        year: data.year || null,
        purchase_price: data.purchase_price ? Number(data.purchase_price) : null,
        shipping_cost: data.shipping_cost ? Number(data.shipping_cost) : null,
        grading_cost: data.grading_cost ? Number(data.grading_cost) : null,
        population_when_bought: data.population_when_bought ? Number(data.population_when_bought) : null,
        current_population: data.current_population ? Number(data.current_population) : null,
        coin_variety_id: data.coin_variety_id ? Number(data.coin_variety_id) : null,
        cac_sticker: Boolean(data.cac_sticker),
        mint_mark: data.mintMark || null // Handle renamed field
      };

      console.log('Formatted data:', formattedData); // Debug log

      const response = await coins.create(formattedData);
      console.log('Create coin response:', response); // Debug log
      navigate(`/coins/${response.id}`);
    } catch (err) {
      console.error('Failed to create coin:', err);
      setError(err.message || 'Failed to create coin. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
    <div className="container mx-auto p-4">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Please log in to add coins</h2>
      </div>
    </div>
    );
  }

  return (
    <div className="container mx-auto px-2 pt-4">
      <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
        <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 ${!isMobile ? 'mb-4' : ''}`}>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add New Coin</h2>
            <p className="text-sm text-gray-600">
              {mode === 'manual' 
                ? 'Enter the details of your new coin below'
                : mode === 'bulk'
                ? 'Import multiple coins from a spreadsheet'
                : 'Import multiple certified coins by certification number'
              }
            </p>
          </div>
          {!isMobile && (
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setMode('manual')}
                className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md ${
                  mode === 'manual'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Manual&nbsp;Entry
              </button>
              <button
                type="button"
                onClick={() => setMode('bulk')}
                className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md ${
                  mode === 'bulk'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Spreadsheet&nbsp;Import
              </button>
              <button
                type="button"
                onClick={() => setMode('batch')}
                className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md ${
                  mode === 'batch'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Batch&nbsp;Import
              </button>
            </div>
          )}
        </div>
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        {mode === 'manual' ? (
          <CoinForm onSubmit={handleSubmit} isLoading={isLoading} />
        ) : mode === 'bulk' ? (
          <BulkImportForm onComplete={() => navigate('/inventory')} />
        ) : (
          <BatchCoinForm />
        )}
      </div>
    </div>
  );
};

export default AddCoin;
